import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TRootState } from "../../store";
import useAxiosClient from "../../hooks/useAxiosClient";
import {
  setAddParameterPanelStatus,
  setAddRulePanelStatus,
  setAllParametersRules,
  setRefreshParametrsEntity,
} from "../../reducers/riskEngineSlice";
import { toast } from "react-toastify";
import {
  TAllParametersRulesArray,
  TAllParametersRulesObj,
} from "../../types/eiskEngine";

const useGetParametersRules = () => {
  const { handleGetCall, handleDeleteCall, handlePutCall } = useAxiosClient();
  const dispatch = useDispatch();

  const {
    adminId,
    addParameterPanelStatus,
    addRulePanelStatus,
    getAllParametesRules,
    activeCategoryTab,
    deletedParametrId,
    refreshParametrsEntity,
    addEditParameterMode,
    adjustedTargetParameters,
    selectedParameter,
    addRuleForSelectedParam,
    addParameterRuleData,
    ruleMode,
    selectedRuleIndex,
  } = useSelector((state: TRootState) => ({
    adminId: state.auth.adminId,
    addParameterPanelStatus:
      state.riskEngineDetailsSlice.addParameterPanelStatus,
    addRulePanelStatus: state.riskEngineDetailsSlice.addRulePanelStatus,
    getAllParametesRules: state.riskEngineDetailsSlice.getAllParametesRules,
    activeCategoryTab: state.riskEngineDetailsSlice.activeCategoryTab,
    deletedParametrId: state.riskEngineDetailsSlice.deletedParametrId,
    refreshParametrsEntity: state.riskEngineDetailsSlice.refreshParametrsEntity,
    addEditParameterMode: state.riskEngineDetailsSlice.addEditParameterMode,
    adjustedTargetParameters:
      state.riskEngineDetailsSlice.adjustedTargetParameters,
    selectedParameter: state.riskEngineDetailsSlice.selectedParameter,
    addRuleForSelectedParam:
      state.riskEngineDetailsSlice.addRuleForSelectedParam,
    addParameterRuleData: state.riskEngineDetailsSlice.addParameterRuleData,
    ruleMode: state.riskEngineDetailsSlice.ruleMode,
    selectedRuleIndex: state.riskEngineDetailsSlice.selectedRuleIndex,
  }));

  const [searchText, setSearchtext] = useState("");
  const [allParamsRefresh, setAllParamsRefresh] = useState(false);
  const [filteredParametersData, setFilteredParameters] = useState<
    TAllParametersRulesObj[]
  >([]);
  const [deleteParameterModal, setDeleteParameterModel] = useState(false);
  const [deleteRuleModal, setDeleteRuleModel] = useState(false);

  //   add parameter panel open
  const onClickAddParameter = (status: boolean) => {
    dispatch(setAddParameterPanelStatus(status));
  };

  //   add rule panel open
  const onClickAddRule = (status: boolean) => {
    dispatch(setAddRulePanelStatus(status));
  };

  //   to get all paramerts and rles
  const getOnoScoreCategoryDetails = () => {
    handleGetCall<TAllParametersRulesObj[]>(
      `solutions/risk-engine/adminId/${adminId}/params/type/${activeCategoryTab}`
    )
      .then(({ data }) => {
        if (data) {
          const updatedData = data.map((param: any) => ({
            ...param,
            status: 2,
            rules: (param.rules || []).map((rule: any) => ({
              ...rule,
              status: 2,
            })),
          }));

          dispatch(setAllParametersRules(updatedData));
        } else {
          dispatch(setAllParametersRules([]));
        }
        setAllParamsRefresh(false);
      })
      .catch((err: any) => {
        setAllParamsRefresh(false);
        dispatch(setAllParametersRules([]));
        // toast.error(err.response.data.status.description);
      });
  };
  useEffect(() => {
    getOnoScoreCategoryDetails();
    setSearchtext("");
  }, [activeCategoryTab, refreshParametrsEntity]);

  //   to filter params
  const filteredParameters = () => {
    let filtered = getAllParametesRules;
    const trimmedSearchText = searchText
      .trim()
      .replace(/[\s.]+/g, "")
      .toLowerCase();

    if (trimmedSearchText.length > 0) {
      filtered = filtered?.filter((option) => {
        const paramName = option.param_name
          ?.toString()
          .toLowerCase()
          .includes(trimmedSearchText);

        return paramName;
      });
    }

    setFilteredParameters(filtered);
  };

  //   search based filter
  useEffect(() => {
    const timer = setTimeout(() => {
      filteredParameters();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText, activeCategoryTab, getAllParametesRules]);

  //   delete parametre api call
  const [adjustedParams, setAdjustedParams] = useState<any[]>([]);
  const deleteParameterFun = (payload: any) => {
    const { deleted_param_id, updated_params } = payload;
    let obj = {
      target:
        updated_params.length > 0
          ? updated_params?.map((param: any) => ({
            param_id: param.param_id,
            param_name: param.param_name,
            weight: param.weight,
            type: param.type,
            sub_type: param.subtype,
            desc: param.desc,
            status: param.status,
            rules: param.rules.map((rule: any) => ({
              rule_id: rule.rule_id,
              rule_name: rule.rule || rule.rule_name,
              score: rule.score,
              status: rule.status,
            })),
          }))
          : [],
    };
    handleDeleteCall({
      URL: `solutions/risk-engine/adminId/${adminId}/params/${deleted_param_id}`,
      apiParams: obj,
    })
      .then(({ data, status }) => {
        toast.success(status.description);
        getOnoScoreCategoryDetails();
        setDeleteParameterModel(false);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const validateAddEditParameter = () => {
    const rules = addParameterRuleData.rules || [];

    if (rules.length > 0) {
      for (let i = 0; i < rules.length; i++) {
        const rule = rules[i];

        if (!rule.rule_name || rule.rule_name.trim() === "") {
          toast.error(`Condition is required for Rule ${i + 1}`);
          return false;
        }
      }
    }
    const weight = Number(addParameterRuleData.weight);
    let validRules = (addParameterRuleData.rules || []).filter(
      (rule) => rule.status !== 0
    );

    if (ruleMode === "delete") {
      validRules = validRules.filter(
        (rule) => rule.rule_id !== selectedRuleIndex
      );
    }
    const scores = (validRules || [])
      .filter((rule) => rule.status !== 0)
      .map((rule) => Number(rule.score || 0));

    const exceedWeight = scores.some((score) => score > weight);
    const hasWeightScore = scores.includes(weight);

    const allSameScores = scores.every((score) => score === scores[0]);

    if (addParameterRuleData?.rules?.length > 0) {
      if (exceedWeight) {
        toast.error(
          `Rule score should not exceed parameter weight (${weight})`
        );
        return false;
      }

      if (!hasWeightScore && ruleMode !== "delete") {
        toast.error(
          `One rule score must be equal to the parameter weight (${weight})`
        );
        return false;
      }
      if (allSameScores && scores.length > 1) {
        toast.error("All rule scores should not be the same");
        return false;
      }
      if (!hasWeightScore && ruleMode === "delete") {
        toast.error(
          `You can't delete heighest score rule, if you want to delete Please adjust other rule score to match the parameter weight (${weight})`
        );
        return false;
      }
    }
    return true;
  };

  const onAddRules = () => {
    if (validateAddEditParameter()) {
      const obj = {
        source: {
          param_id: addRuleForSelectedParam?.param_id,
          param_name: addRuleForSelectedParam?.param_name,
          type: addRuleForSelectedParam?.type,

          desc: addRuleForSelectedParam?.desc,
          status: 2,
          weight: addRuleForSelectedParam?.weight
            ? Number(addRuleForSelectedParam?.weight)
            : 0,
          sub_type: "",
          rules: addParameterRuleData.rules.map((rule: any) => ({
            rule_id: rule.rule_id,
            rule_name: rule.rule || rule.rule_name,
            score: rule.score,
            status:
              ruleMode === "delete" && rule.rule_id === selectedRuleIndex
                ? 0
                : rule.status,
          })),
        },
        target: [],
      };
      handlePutCall({
        URL: `solutions/risk-engine/adminId/${adminId}/params/rules`,
        apiParams: obj,
      })
        .then(({ data, status }) => {
          toast.success(status.description);
          dispatch(setAddParameterPanelStatus(false));
          dispatch(setRefreshParametrsEntity());
          onClickAddRule(false);
        })
        .catch((err: any) => { });
    }
  };

  return {
    addParameterPanelStatus,
    onClickAddParameter,
    addRulePanelStatus,
    onClickAddRule,
    getAllParametesRules,
    activeCategoryTab,
    filteredParametersData,
    setSearchtext,
    searchText,
    deleteParameterFun,
    deletedParametrId,
    deleteParameterModal,
    setDeleteParameterModel,
    setDeleteRuleModel,
    deleteRuleModal,
    addEditParameterMode,
    adjustedTargetParameters,
    setAdjustedParams,
    adjustedParams,
    onAddRules,
    setAllParamsRefresh,
    allParamsRefresh,
    ruleMode,
  };
};
export default useGetParametersRules;
